"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BasinClient = exports.BasinClientOptions = void 0;
const api = require("./helpers/api");
const url_1 = require("./helpers/url");
const Marketboard_1 = require("./models/Marketboard");
const Memo_1 = require("./models/Memo");
const Memo_2 = require("./models/Memo");
const Scan_1 = require("./models/Scan");
const Scans_1 = require("./models/Scans");
const jwt_decode_1 = require("jwt-decode");
const ApiResponse_1 = require("./models/ApiResponse");
const Positions_1 = require("./models/Positions");
const debounce_1 = require("./helpers/debounce");
const REPLACE_ALL_BOARDS_DEBOUNCE_MS = 500;
/**
 * The Basin Client Options
 * Monex Subdomain is dm
 * Encrypted User ID is rz
 * @export
 * @class BasinClientOptions
 */
class BasinClientOptions {
}
exports.BasinClientOptions = BasinClientOptions;
/**
 * The Basin Client class. Provides methods to access the Basin API.
 *
 * @export
 * @class BasinClient
 */
class BasinClient {
    constructor(options) {
        var _a, _b;
        // Auxiliar function used to replace all the user boards with debounce.
        this.replaceAllBoardsDebounced = (0, debounce_1.debounce)(this.replaceAllBoards, REPLACE_ALL_BOARDS_DEBOUNCE_MS);
        this._apiUrl = options.apiUrl;
        this._token = options.token;
        this._monexSubdomain = (_a = options.monexSubdomain) !== null && _a !== void 0 ? _a : null;
        this._encryptedUserId = (_b = options.encryptedUserId) !== null && _b !== void 0 ? _b : null;
    }
    /**
     * Creates a particular user
     * https://tsgjira.atlassian.net/wiki/spaces/MI/pages/128484917/Users+endpoints#Usersendpoints-PUT/user/%3Cuser-id%3E/create
     * PUT /user/<user-id>/create
     *
     * @return {*}  {Promise<boolean>}
     * @memberof Basin
     */
    async createUser() {
        const userId = this.getUserId();
        const decodedHeader = (0, jwt_decode_1.default)(this._token);
        const body = {
            data: {
                user: {
                    id: userId,
                    encryptedId: decodedHeader['encryptedId'],
                    dm: decodedHeader['dm'],
                    rf: decodedHeader['rf'],
                    rz: decodedHeader['rz'],
                    attrSrcKey: decodedHeader['attrSrcKey']
                }
            }
        };
        await api.put((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/create`), JSON.stringify(body), this._token);
        return true;
    }
    /**
     * Returns a user marketboard for a particular user
     *
     * @param {boardId} boardId
     * @return {*}  {Promise<ApiResponse<MarketboardResponse>>}
     * @memberof Basin
     */
    async getMarketboard(boardId) {
        const userId = this.getUserId();
        const result = await api.get((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/board/${boardId}`), this._token);
        const MarketboardResponse = {
            marketboardLastUpdate: result.marketboardLastUpdate || null,
            board: new Marketboard_1.Marketboard(result.marketboard)
        };
        return new ApiResponse_1.ApiResponse(result, MarketboardResponse);
    }
    /**
     * Returns a list of user marketboards for a particular user
     * https://tsgjira.atlassian.net/wiki/spaces/MI/pages/128484917/Users+endpoints#Usersendpoints-GET/user/%3Cuser-id%3E/boards
     * GET /user/<user-id>/boards
     *
     * @param {format?} format
     * @param {number?} top
     * @param {number?} skip
     * @return {*}  {Promise<ApiResponse<MarketboardsResponse[]>>}
     * @memberof Basin
     */
    async getMarketboards(format, top, skip) {
        const userId = this.getUserId();
        const queryParameters = this._getQueryParametersForMarketboards(format, top, skip);
        const results = await this.getMarketboardsWithRetry(userId, queryParameters, true);
        // Since the `ts` format returns the boards with the `Marketboards` property and the
        // non-`ts` format (Galaxy) returns the boards with the `boards` property, we need to
        // specify which property to use from the results when extracting the marketboards
        const property = results.Marketboards ? 'Marketboards' : 'boards';
        const marketboards = results[property].map((board) => new Marketboard_1.Marketboard(board));
        const MarketboardsResponse = {
            marketboardLastUpdate: results.marketboardLastUpdate || null,
            boards: marketboards
        };
        return new ApiResponse_1.ApiResponse(results, MarketboardsResponse);
    }
    /**
     * Returns the marketboards from a particular user
     * @private
     * @param {string} userId
     * @param {string} queryParameters
     * @param {boolean} shouldRetry
     * @return {*}  {any}
     * @memberof Basin
     */
    async getMarketboardsWithRetry(userId, queryParameters, shouldRetry) {
        try {
            return await api.get((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/boards${queryParameters}`), this._token);
        }
        catch (e) {
            // If it should not retry or if the error is not `USER_NOT_FOUND`, throw the error
            if (!shouldRetry || e.message !== 'USER_NOT_FOUND') {
                throw e;
            }
            await this.createUser();
            return this.getMarketboardsWithRetry(userId, queryParameters, false);
        }
    }
    /**
     * Returns the userId obtained from the token
     * @private
     * @return {*}  {string}
     * @memberof Basin
     */
    getUserId() {
        const decodedHeader = (0, jwt_decode_1.default)(this._token);
        return decodedHeader['uid'];
    }
    /**
     * Gets query parameters for the get marketboards request
     * @private
     * @param {format?} format
     * @param {number?} top
     * @param {number?} skip
     * @return {*}  {string}
     * @memberof Basin
     */
    _getQueryParametersForMarketboards(format, top, skip) {
        let url = '';
        if (format) {
            url += `?format=${format}`;
        }
        if (top) {
            url += url ? '&' : '?';
            url += `top=${top}`;
        }
        if (skip) {
            url += url ? '&' : '?';
            url += `skip=${skip}`;
        }
        return url;
    }
    /**
     * Adds a new user marketboard for a particular user
     *
     * @param {marketboard} marketboard
     * @return {*}  {Promise<ApiResponse<MarketboardResponse>>}
     * @memberof Basin
     */
    async addMarketboard(marketboard) {
        const userId = this.getUserId();
        const body = {
            data: {
                marketboard: marketboard
            }
        };
        const result = await api.post((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/board`), JSON.stringify(body), this._token);
        const MarketboardResponse = {
            marketboardLastUpdate: result.marketboardLastUpdate || null,
            board: new Marketboard_1.Marketboard(result.marketboard)
        };
        return new ApiResponse_1.ApiResponse(result, MarketboardResponse);
    }
    /**
     * Updates a marketboard for a particular user
     *
     * @param {marketboard} marketboard
     * @return {*}  {Promise<ApiResponse<MarketboardResponse>>}
     * @memberof Basin
     */
    async updateMarketboard(boardId, marketboard) {
        const userId = this.getUserId();
        const body = {
            data: {
                marketboard: marketboard
            }
        };
        const result = await api.put((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/board/${boardId}`), JSON.stringify(body), this._token);
        const MarketboardResponse = {
            marketboardLastUpdate: result.marketboardLastUpdate || null,
            board: new Marketboard_1.Marketboard(result.marketboard)
        };
        return new ApiResponse_1.ApiResponse(result, MarketboardResponse);
    }
    /**
     * Auxiliar function used to replace all the user boards. Used for the debounce logic below.
     *
     * @param {Marketboard[]} marketboards
     * @param {number?} marketboardLastUpdate
     * @return {*}  {Promise<MarketboardsResponse>}
     * @memberof Basin
     */
    async replaceAllBoards(marketboards, marketboardLastUpdate) {
        const userId = this.getUserId();
        const body = {
            data: {
                Marketboards: marketboards,
                marketboardLastUpdate: marketboardLastUpdate
            }
        };
        // In case the marketboardLastUpdate is not provided, delete it
        if (!marketboardLastUpdate) {
            delete body.data.marketboardLastUpdate;
        }
        const results = await api.post((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/boards`), JSON.stringify(body), this._token);
        const boards = results.Marketboards.map((board) => new Marketboard_1.Marketboard(board));
        const MarketboardsResponse = {
            marketboardLastUpdate: results.marketboardLastUpdate,
            boards: boards
        };
        return new ApiResponse_1.ApiResponse(results, MarketboardsResponse);
    }
    /**
     * Saves a list of user marketboards for a particular user
     * https://tsgjira.atlassian.net/wiki/spaces/MI/pages/128484917/Users+endpoints#Usersendpoints-POST/user/%3Cuser-id%3E/boards
     * POST /user/<user-id>/boards
     *
     * @param {Marketboard[]} marketboards
     * @return {*}  {Promise<MarketboardsResponse>}
     * @memberof Basin
     */
    async saveMarketboards(marketboards, marketboardLastUpdate) {
        return this.replaceAllBoardsDebounced(marketboards, marketboardLastUpdate);
    }
    /**
     * Deletes a user marketboard for a particular user
     *
     * @param {boardId} boardId
     * @return {*}  {Promise<any>}
     * @memberof Basin
     */
    async deleteMarketboard(boardId) {
        const userId = this.getUserId();
        const result = await api.deleteRequest((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/board/${boardId}`), this._token);
        return result;
    }
    /**
     * Gets a list of user scans for a particular user
     * https://confluence.tradestation.com/display/MI/Users+endpoints#Usersendpoints-GET/user/%3Cuser-id%3E/scans
     * GET /user/<user-id>/scans
     *
     * @return {*}  {Promise<Scans>}
     * @memberof Basin
     */
    async getScans() {
        const userId = this.getUserId();
        const results = await api.get((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/scans`), this._token);
        const scans = new Scans_1.Scans(results);
        return Promise.resolve(scans);
    }
    /**
     * Replaces the user scans for a particular user
     * https://confluence.tradestation.com/display/MI/Users+endpoints#Usersendpoints-PUT/user/%3Cuser-id%3E/scans
     * PUT /user/<user-id>/scans
     *
     * @param {Scan[]} scans
     * @return {*}  {Promise<any>}
     * @memberof Basin
     */
    async replaceScans(scans) {
        const userId = this.getUserId();
        const result = await api.put((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/scans`), JSON.stringify(scans), this._token);
        return result;
    }
    /**
     * Replaces the user scans for a particular user
     * https://confluence.tradestation.com/display/MI/Users+endpoints#Usersendpoints-POST/user/%3Cuser-id%3E/scans
     * POST /user/<user-id>/scans
     *
     * @param {Scan} scan
     * @return {*}  {Promise<any>}
     * @memberof Basin
     */
    async addScan(scan) {
        const userId = this.getUserId();
        const result = await api.post((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/scans`), JSON.stringify(scan), this._token);
        return new Scan_1.Scan(result.scan);
    }
    /**
     * Gets a single scan object from the user's list of saved scans
     * https://confluence.tradestation.com/display/MI/Users+endpoints#Usersendpoints-GET/user/%3Cuser-id%3E/scans/%3Cscan-id%3E
     * GET /user/<user-id>/scans/<scan-id>
     *
     * @param {string} scanId
     * @return {*}  {Promise<Scan>}
     * @memberof Basin
     */
    async getScan(scanId) {
        const userId = this.getUserId();
        const results = await api.get((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/scans/${scanId}`), this._token);
        return Promise.resolve(new Scan_1.Scan(results.scan));
    }
    /**
     * Replaces a specific user scan for a particular user
     * https://confluence.tradestation.com/display/MI/Users+endpoints#Usersendpoints-PUT/user/%3Cuser-id%3E/scans/%3Cscan-id%3E
     * PUT /user/<user-id>/scans/<scan-id>
     * @param {string} scanId
     * @param {Scan[]} scans
     * @return {*}  {Promise<any>}
     * @memberof Basin
     */
    async replaceScan(scanId, scan) {
        const userId = this.getUserId();
        const result = await api.put((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/scans/${scanId}`), JSON.stringify(scan), this._token);
        return result;
    }
    /**
     * Deletes a single scan object in the user's list of saved scans
     * https://confluence.tradestation.com/display/MI/Users+endpoints#Usersendpoints-DELETE/user/%3Cuser-id%3E/scans/%3Cscan-id%3E
     * DELETE /user/<user-id>/scans/<scan-id>
     * @param {string} scanId
     * @return {*}  {Promise<any>}
     * @memberof Basin
     */
    async deleteScan(scanId) {
        const userId = this.getUserId();
        const result = await api.deleteRequest((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/scans/${scanId}`), this._token);
        return result;
    }
    /**
     * Gets a list of user memos for a particular user
     * https://confluence.tradestation.com/display/MI/Users+endpoints#Usersendpoints-GET/user/%3Cuser-id%3E/memo
     * GET /user/<user-id>/memo
     *
     * @return {*}  {Promise<Memo[]>}
     * @memberof Basin
     */
    async getMemos() {
        const userId = this.getUserId();
        const results = await api.get((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/memo`), this._token);
        return results.memos.map((m) => new Memo_1.Memo(m));
    }
    /**
     * Gets array of memos (If found) for symbols contained in the supplied array
     * If the user has a memo for a symbol, it will contain that memo object
     * Otherwise, that symbol will be an empty memo object
     *
     * @param {Array<string>} symbolIds
     * @return {*}  {Promise<Memo[]>}
     * @memberof Basin
     */
    async getMemosBySymbols(symbolIds) {
        const userId = this.getUserId();
        const symbolsWithMemo = await api.post((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/memo`), { symbols: symbolIds }, this._token);
        return symbolsWithMemo.memos.map((m) => new Memo_1.Memo(m));
    }
    /**
     * Adds a user memo for a specific symbol
     * https://confluence.tradestation.com/display/MI/Users+endpoints#Usersendpoints-POST/user/%3Cuser-id%3E/memo/%3Csymbol-id%3E
     * POST /user/<user-id>/memo/<symbol-id>
     *
     * @param {string} symbolId
     * @param {string} memo
     * @return {*}  {Promise<MemoPost>}
     * @memberof Basin
     */
    async addMemoBySymbol(symbolId, memo) {
        const userId = this.getUserId();
        const response = await api.post((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/memo/${symbolId}`), memo, this._token);
        return new Memo_2.MemoPost(response.memo);
    }
    /**
     * Gets a single memo object (If found) for a specific symbol
     *
     * @param {string} symbolId
     * @return {*}  {Promise<Memo>}
     * @memberof Basin
     */
    async getMemoBySymbol(symbolId) {
        const userId = this.getUserId();
        const response = await api.get((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/memo/${symbolId}`), this._token);
        return new Memo_1.Memo(response);
    }
    /**
     * Deletes a single memo object for a specific symbol
     * https://confluence.tradestation.com/display/MI/Users+endpoints#Usersendpoints-DELETE/user/%3Cuser-id%3E/memo/%3Csymbol-id%3E
     * DELETE /user/<user-id>/memo/<symbol-id>
     * @param {string} symbolId
     * @return {*}  {Promise<void>}
     * @memberof Basin
     */
    async deleteMemoBySymbol(symbolId) {
        const userId = this.getUserId();
        await api.deleteRequest((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/memo/${symbolId}`), this._token);
    }
    /**
     * Gets user's posistions
     * GET user/${userId}/positions?rz=${encryptedUserId}&dm=${monexSubdomain}
     *
     * @param {boolean} detailed
     * @return {*}  {Promise<Positions>}
     * @memberof Basin
     */
    async getPositions(detailed = false) {
        const userId = this.getUserId();
        // Note that, although the encryptedUserId and monexSubdomain can be grabbed from the token, the team
        // decided not to use them from the token but instead to use the values that are passed to the constructor,
        // just in case the generated token doesn't contain them.
        if (!this._encryptedUserId) {
            throw new Error('RZ_MISSING');
        }
        if (!this._monexSubdomain) {
            throw new Error('DM_MISSING');
        }
        const results = await api.get((0, url_1.buildFullyQualifiedUrl)(this._apiUrl, `user/${userId}/positions?rz=${this._encryptedUserId}&dm=${this._monexSubdomain}&detailed=${detailed}`), this._token);
        return new Positions_1.Positions(results.positions);
    }
}
exports.BasinClient = BasinClient;
//# sourceMappingURL=basin.js.map